import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Unsaved work, and the two ways an admin screen loses it.
 *
 * Plan 008 asks for an `EntityForm` primitive with "dirty tracking and an
 * unsaved-changes guard". This is the guard half, as a hook rather than a
 * wrapper component: the editors in this app differ in layout, tabs and
 * validation but agree exactly on this one behaviour, and a component that
 * owned their markup would have to grow a prop for every difference.
 *
 * CALL `useEditorExitGuard` INSTEAD, not this. The product, brand and
 * spec-template editors all reach this hook through
 * `features/catalog/lib/use-editor-exit-guard`, which composes it with the
 * in-app half. Wiring an editor to this hook alone protects it against the exit
 * nobody takes and leaves it open on the one they do.
 *
 * THE TWO EXITS ARE NOT THE SAME. Closing the tab is the browser's to refuse,
 * and it only listens to `beforeunload`. Navigating inside the SPA never
 * touches that event -- React Router just unmounts the editor. Only
 * `beforeunload` is handled HERE: the in-app half needs `useBlocker`, which
 * needs a data router, and this layer does not depend on one.
 *
 * `isDirty` is the caller's to compute, deliberately. Comparing a form to its
 * loaded values is the one part that is genuinely per-editor -- a product knows
 * that `specifications` is an object and that `updatedAt` moving is not an
 * edit -- and a generic deep-equal here would report every editor permanently
 * dirty on its own timestamps.
 */
export function useUnsavedChangesGuard({
	isDirty,
	message = "You have unsaved changes. Leave without saving?",
}: {
	isDirty: boolean;
	message?: string;
}): {
	/**
	 * Wrap any in-app navigation. Returns false and leaves you where you are if
	 * the operator declines.
	 */
	/**
	 * Confirm before a navigation the CALLER initiates.
	 *
	 * NO PRODUCTION CALLER, and do not add one alongside `useEditorExitGuard`:
	 * that hook's blocker already intercepts every in-app navigation, including
	 * the ones an editor makes itself, so wrapping a `navigate()` in this as well
	 * prompts twice for one departure. It survives for a caller that wants the
	 * `beforeunload` half without a data router.
	 */
	confirmLeave: () => boolean;
} {
	// Through a ref so the listener does not have to be torn down and rebuilt on
	// every keystroke, which is what `isDirty` in the dependency list would mean.
	const dirtyRef = useRef(isDirty);
	dirtyRef.current = isDirty;

	useEffect(() => {
		const onBeforeUnload = (event: BeforeUnloadEvent) => {
			if (!dirtyRef.current) return;
			// Browsers ignore custom text now and show their own wording; what
			// matters is that preventDefault happens at all.
			event.preventDefault();
			event.returnValue = "";
		};
		window.addEventListener("beforeunload", onBeforeUnload);
		return () => window.removeEventListener("beforeunload", onBeforeUnload);
	}, []);

	const confirmLeave = useCallback(() => {
		if (!dirtyRef.current) return true;
		return window.confirm(message);
	}, [message]);

	return { confirmLeave };
}

/**
 * Dirty tracking against the values a form was LOADED with.
 *
 * The subtlety this exists for: "dirty" means different from what the server
 * sent, not different from what it was a render ago. An editor that tracks
 * per-field touched flags calls itself dirty after someone types a character
 * and deletes it, and then interrupts them on the way out for nothing.
 *
 * `reset` is what a successful save calls -- the new baseline is what was just
 * persisted, and without it every editor stays dirty forever after its first
 * save.
 */
export function useDirtyState<T>(loaded: T): {
	baseline: T;
	isDirty: (current: T) => boolean;
	reset: (next: T) => void;
} {
	const [baseline, setBaseline] = useState(loaded);

	// The form is re-seeded when a different entity is loaded into the same
	// mounted editor -- navigating product A -> product B keeps the component.
	// Without this the baseline stays on A and B is dirty the moment it renders.
	const loadedRef = useRef(loaded);
	useEffect(() => {
		if (loadedRef.current !== loaded) {
			loadedRef.current = loaded;
			setBaseline(loaded);
		}
	}, [loaded]);

	return {
		baseline,
		isDirty: (current: T) =>
			JSON.stringify(current) !== JSON.stringify(baseline),
		reset: setBaseline,
	};
}
